<template>
  <el-container
    v-if="selectedChat"
    v-loading="isLoading || busyStates[selectedChat?.RowKey]?.uploading || scanningVirus"
    :element-loading-text="loadingText"
    id="mainchat"
    :style="mainChatStyle"
  >
    <VisitorProfile
      v-if="dataCollectionFormEnabled"
      :style="visitorProfileStyle"
      :formData="selectedChatformData"
      ref="visitor-profile"
      class="visitorprofile"
      @toggleFormData="toggleFormData"
    />

    <el-header class="interactions-row" style="padding: 10px 10px 0 10px">
      <el-row
        type="flex"
        style="align-items: center; justify-content: space-between; flex-direction: row"
      >
        <div style="display: flex; flex-direction: row">
          <div>
            <span style="font-weight: bold">
              {{ getChatName(selectedChat) }}
              <el-tooltip
                effect="dark"
                content="Shows values in the following order: Full Name, Mobile Number, Email, Chat ID"
                placement="right"
              >
                <i class="has-text-grey el-icon-question" style="margin-left: 0.5em" />
              </el-tooltip>
            </span>
            <FromNow
              style="font-size: 0.7em; color: grey; margin-bottom: 3px"
              :value="lastSentMessageTimeStamp"
              :interval="5000"
              :prefixMessage="'last update '"
            />
          </div>
        </div>

        <div class="chat-actions-header secondary-header">
          <div
            style="margin-bottom: 0; display: flex; align-items: center; flex-wrap: wrap; gap: 5px"
          >
            <!-- Timeline Components -->
            <div style="display: flex; flex-direction: row; place-items: center; gap: 10px">
              <EscalationTimeline :chat="selectedChat" />
              <UserQueryTopicTimeline :chat="selectedChat" />
            </div>

            <!-- Main Action Buttons -->
            <div class="chat-actions">
              <!-- Start Chat Button (Queue Tab) -->
              <template v-if="isQueueTab && !isInChat">
                <el-button
                  type="warning"
                  size="mini"
                  icon="el-icon-chat-dot-round"
                  :disabled="agentIsNotOnline || isBusy(selectedChat?.RowKey)"
                  :loading="busyStates[selectedChat?.RowKey]?.isJoiningChat"
                  @click.native="joinLivechat(selectedChat)"
                >
                  Start {{ $device.Mobile ? "" : "conversation" }}
                </el-button>
              </template>

              <!-- Start Chat Button (Monitor Tab) -->
              <template v-else-if="isMonitorTab">
                <el-button
                  size="mini"
                  type="warning"
                  :disabled="
                    agentIsNotOnline || isBusy(selectedChat?.RowKey) || differentDepartment
                  "
                  :loading="busyStates[selectedChat?.RowKey]?.isJoiningChat"
                  @click.native="sendToQueue()"
                >
                  Start {{ $device.Mobile ? "" : "conversation" }}
                </el-button>
                <el-tooltip
                  effect="dark"
                  content="Disabled, if sending to queue in process, or in offline status, or different department with user"
                  placement="right"
                  style="margin-left: 4px"
                >
                </el-tooltip>
              </template>

              <!-- Resolve and Leave Buttons (Queue Tab, when in chat) -->
              <template v-if="isQueueTab && isInChat && !dataCollectionFormEnabled">
                <el-button
                  plain
                  type="success"
                  size="mini"
                  icon="el-icon-check"
                  style="margin-left: 0"
                  :disabled="!canAgentReceiveNewMessage || isBusy(selectedChat?.RowKey)"
                  :loading="busyStates[selectedChat?.RowKey]?.isResolvingChat"
                  @click.native="setResolve"
                >
                  Resolve chat
                </el-button>
                <el-button
                  plain
                  type="primary"
                  size="mini"
                  icon="el-icon-close"
                  style="margin-left: 0"
                  :disabled="!canAgentReceiveNewMessage || isBusy(selectedChat?.RowKey)"
                  :loading="busyStates[selectedChat?.RowKey]?.isLeavingChat"
                  @click.native="leaveLivechat(selectedChat)"
                  v-if="
                    (isRoutingBroadcastMode && isInChat) ||
                    (isRoutingAssignmentMode && hasMoreThanOneAgents)
                  "
                >
                  Leave chat
                </el-button>

                <!-- Invite Agent -->
                <InviteAgent
                  v-if="isQueueTab"
                  :selected-chat="selectedChat"
                  :is-busy="isBusy(selectedChat?.RowKey)"
                />
              </template>

              <!-- Export Chat Button -->
              <el-tooltip
                class="item"
                effect="dark"
                content="Download selected chat data"
                placement="left"
              >
                <el-dropdown trigger="click" @command="handleExportChat">
                  <el-button
                    plain
                    style="margin-left: 0"
                    icon="el-icon-download"
                    circle
                    size="mini"
                  />
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="xlsx">.xlsx file</el-dropdown-item>
                    <el-dropdown-item command="txt">.txt file</el-dropdown-item>
                    <el-dropdown-item command="pdf">.pdf file</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </el-tooltip>

              <!-- Close Chat Button -->
              <el-tooltip class="item" effect="dark" content="Close this chat" placement="left">
                <el-button
                  plain
                  type="danger"
                  style="margin-left: 0"
                  icon="el-icon-close"
                  circle
                  size="mini"
                  @click="closeCurrentChat"
                />
              </el-tooltip>
              <el-tooltip
                v-if="!$device.Mobile"
                class="item"
                effect="dark"
                :content="sidebarToggleTooltip"
                placement="left"
              >
                <el-button
                  plain
                  style="margin-left: 0"
                  :icon="sidebarVisible ? 'el-icon-s-fold' : 'el-icon-s-unfold'"
                  circle
                  size="mini"
                  @click="toggleSidebar"
                />
              </el-tooltip>
              <el-tooltip v-if="shouldShowExternalTool" class="item" effect="dark" content="Open More Tool" placement="left">
                <el-button
                  type="text"
                  style="margin-left: 0"
                  icon="el-icon-arrow-left"
                  @click="toolDrawer = true"
                />
              </el-tooltip>

              <!-- More Actions Dropdown -->
              <el-tooltip
                class="item"
                effect="dark"
                content="More actions"
                placement="left"
                v-if="shouldShowMoreActions"
              >
                <el-dropdown trigger="click" >
                  <el-button
                    plain
                    type="primary"
                    style="margin-left: 0"
                    circle
                    size="mini"
                    icon="el-icon-more"
                  ></el-button>
                  <el-dropdown-menu slot="dropdown">
                    <!-- Escalate Chat -->
                    <el-dropdown-item v-if="isQueueTab && isHandoverRoutingByDepartmentEnabled">
                      <EscalateChat
                        :is-busy="isBusy(selectedChat?.RowKey)"
                        :selected-chat="selectedChat"
                        :is-in-chat="isInChat"
                      />
                    </el-dropdown-item>

                    <!-- Co-browsing -->
                    <el-dropdown-item
                      v-if="
                        isQueueTab && isInChat && isCobrowsingEnabled && channelName === 'webchat'
                      "
                    >
                      <el-button
                        plain
                        type="info"
                        size="mini"
                        :icon="inCobrowseSession ? 'el-icon-phone-outline' : 'el-icon-phone'"
                        :disabled="isBusy(selectedChat?.RowKey) || inCobrowseSession"
                        @click.native="startAndConnectCobrowse()"
                      >
                        {{ inCobrowseSession ? "Ongoing Co-browsing..." : "Start Co-browsing" }}
                      </el-button>
                    </el-dropdown-item>

                    <!-- Form Enabled Status -->
                    <el-dropdown-item v-if="isQueueTab && dataCollectionFormEnabled">
                      <FormEnabledStatus />
                    </el-dropdown-item>

                    <!-- Zoom Meeting -->
                    <el-dropdown-item v-if="isQueueTab && isInChat && this.$store.state.modules.zoom.enabled">
                      <ZoomMeetingButton :selected-chat="selectedChat" />
                    </el-dropdown-item>

                    <!-- Data Collection Form Toggle -->
                    <el-dropdown-item v-if="dataCollectionFormEnabled">
                      <el-button
                        id="flashing-button"
                        :icon="collapseIcon"
                        plain
                        style="width: 100%"
                        type="info"
                        size="mini"
                        @click="toggleFormData"
                      >
                        Collect Data
                      </el-button>
                    </el-dropdown-item>

                    <!-- Enable Notification -->
                    <el-dropdown-item v-if="!isNotificationEnable">
                      <el-button
                        type="info"
                        size="mini"
                        plain
                        icon="el-icon-close-notification"
                        @click="enableNotification"
                        >Enable Notification</el-button
                      >
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </el-tooltip>
            </div>
          </div>
        </div>
      </el-row>

      <!-- Tags -->
      <!-- <Tags
        :state-variables="stateVariables"
        :selected-chat="selectedChat"
        :is-queue-tab="isQueueTab"
        :is-resolved-tab="isResolvedTab"
      /> -->
      <el-row v-if="$device.Mobile">
        <el-collapse-transition>
          <div v-show="showUserDetails">
            <el-divider></el-divider>
            <UserDetails :selected-chat="selectedChat" :value="userDetails" />
            <el-divider></el-divider>
          </div>
        </el-collapse-transition>
      </el-row>
    </el-header>

    <PurgeBanner />
    <el-header
      v-show="inCobrowseSession && cobrowseFrameUrl.includes(selectedChat.user_id)"
      class="secondary-header cobrowse-banner"
    >
      You are in ongoing Co-browsing session,
      <span @click="cobrowseDrawerOpen = true">click here</span>
      to open the session, or close this banner to stop
      <i @click="stopCobrowse()" class="el-icon-close"></i>
    </el-header>

    <!-- Messages of the chat -->
    <el-main ref="chatWindow" class="chat-window" style="padding-bottom: 10px">
      <div style="width: 100%">
        <div class="disabled-conversation-container" v-if="shouldHideConversationHistory">
          <p class="disabled-conversation-text">
            Chat conversation will be available when this chat is assigned to you.
          </p>
        </div>
        <div style="height: 100%; display: flex; flex-direction: column;">
          <div ref="chatWidget" class="chat-widget" style="flex: 1; min-height: 0;" v-if="!shouldHideConversationHistory">
            <div class="prev-conversation" style="text-align: center;">
              <el-button
                plain
                class="more-conversation-button"
                v-if="showMoreConversationButton"
                :loading="busyStates[selectedChat?.RowKey]?.isLoadingMoreConversation"
                @click="showMoreConversation(selectedChat)"
              >
                - show more conversations -
              </el-button>
            </div>

            <el-row
              style="margin-bottom: 0"
              v-for="message in selectedChatInteractions"
              :key="`message-${message.PartitionKey}-${message.RowKey}`"
            >
              <!-- {{message}} -->
              <!-- CHAT MESSAGE -->
              <div
                :style="{
                  maxWidth: '700px',
                  float: isMessageByUser(message.type) ? 'left' : 'right',
                }"
              >
                <!-- DISCONNECTED ALERT -->
                <el-alert
                  v-if="isDisconnectMessage(message)"
                  :title="message.data.content[0].text"
                  class="connection-alert-box"
                  type="error"
                  effect="dark"
                  show-icon
                  :closable="false"
                >
                  <template v-slot:default>
                    <div v-if="$store.state.showAdvanced" class="connection-alert-detail">
                      {{ `Reason: ${message.error}` }}
                    </div>
                    <div class="connection-alert-timestamp">
                      <el-tooltip class="item" effect="dark" content="Sent" placement="top-start">
                        <i class="el-icon-circle-check"></i>
                      </el-tooltip>
                      {{ formatMessageTimestamp(message.date_created) }}
                    </div>
                  </template>
                </el-alert>

                <!-- RECONNECTED ALERT -->
                <el-alert
                  v-else-if="isReconnectMessage(message)"
                  :title="message.data.content[0].text"
                  class="connection-alert-box"
                  type="success"
                  effect="dark"
                  show-icon
                  :closable="false"
                >
                  <template v-slot:default>
                    <div class="connection-alert-timestamp">
                      <el-tooltip class="item" effect="dark" content="Sent" placement="top-start">
                        <i class="el-icon-circle-check"></i>
                      </el-tooltip>
                      {{ formatMessageTimestamp(message.date_created) }}
                    </div>
                  </template>
                </el-alert>

                <span v-else>
                  <message
                    :key="message.RowKey"
                    :ref="`message-${message.RowKey}`"
                    :message="message"
                    :has-more-than-one-agent-in-chat="hasMoreThanOneAgentInChat"
                    class="message-border"
                    :class="getPulseClass(message.RowKey)"
                    @updatePulseRowKey="updatePulseRowKey"
                    font="Tahoma"
                    agent-bubble-color="#F0F0F0"
                    agent-text-color="#4b4f56"
                    user-bubble-color="#0084FF"
                    user-text-color="#fff"
                    button-outline-color="black"
                  />
                </span>
              </div>
            </el-row>

            <el-row>
              <el-col style="margin: 0px 25px 0px 25px">
                <img
                  v-show="getTypingIndicator"
                  src="../../assets/typing_indicator.gif"
                  style="width: 30px"
                />
              </el-col>

              <div class="more-conversation">
              <el-button plain class="more-conversation-button" v-if="showNewerConversationButton"
                :loading="busyStates[selectedChat?.RowKey]?.isLoadingMoreConversation"
                @click="showMoreConversation(selectedChat, true, false, true)">
                - show newer conversations -
              </el-button>
              </div>
            </el-row>
          </div>

          <el-row
            style="padding: 5px; margin-bottom: 0; align-items: flex-end; gap: 10px; flex-shrink: 0;"
            v-if="isInChat"
            type="flex"
          >
            <div style="flex: 1" class="keyreply-livechat-textbox-section">
              <div style="display: flex; gap: 5px; align-items: center">
                <HelperActions
                  v-if="isHelperActionsEnabled"
                  :selected-chat-user-id="selectedChat.user_id"
                  :selected-chat-partition-key="selectedChat.PartitionKey"
                />
                <el-button
                  v-if="isUploadable"
                  :disabled="
                    !isInChat || isBusy(selectedChat?.RowKey) || !canAgentReceiveNewMessage
                  "
                  icon="el-icon-paperclip"
                  type="primary"
                  plain
                  circle
                  @click="fileUpload"
                />
              </div>
              <QuickReplies
                v-if="isLivechatQuickRepliesEnabled"
                :chat-interactions="selectedChatInteractions"
                :selected-chat-user-id="selectedChat.user_id"
                :selected-chat-partition-key="selectedChat.PartitionKey"
              />
              <el-autocomplete
                v-if="$store.state.modules.system.livechatReplySuggestions"
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 5 }"
                style="margin-right: 5px; width: 100%; font-family: inherit"
                ref="textbox"
                class="autocompleteTextbox message-textbox"
                v-model="replyMessage"
                placeholder="Start chatting..."
                popper-class="livechat-message-suggestion"
                :debounce="500"
                :fetch-suggestions="getShortcuts"
                :disabled="isBusy(selectedChat?.RowKey)"
                @select="handleShortcutSelection"
                @keydown.enter.native.exact="handleSendEvent"
              >
                <!-- <el-input ref="agent-input" type="textarea" placeholder="message" v-if="isInChat" v-model="selectedChat.reply" @keydown.enter.native="handleTextarea"> -->
                <template slot-scope="props">
                  <strong class="command">/{{ props.item.name }}</strong>
                  <span>
                    {{
                      Array.isArray(props.item.message) ? props.item.message[0] : props.item.message
                    }}
                  </span>
                </template>
                <!-- </el-input> -->
              </el-autocomplete>

              <el-input
                v-else
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 5 }"
                style="margin-right: 5px; width: 100%"
                ref="textbox"
                class="message-textbox"
                v-model="replyMessage"
                placeholder="Start chatting..."
                :disabled="isBusy(selectedChat?.RowKey)"
                @keydown.enter.native.exact="handleSendEvent"
              ></el-input>

              <el-tooltip class="item" effect="dark" placement="top-end">
                <div slot="content">
                  Hit [⏎ Enter] to send message or [⇧ Shift] + [⏎ Enter] to add a new line to your
                  message
                </div>

                <button
                  style="
                    font-size: 24px;
                    cursor: pointer;
                    color: #1d57d8;
                    margin-right: 20px;
                    border: none;
                    background: none;
                  "
                  @click="handleSendEvent"
                  :disabled="isBusy(selectedChat?.RowKey)"
                >
                  <i class="el-icon-s-promotion"></i>
                </button>
              </el-tooltip>
            </div>

            <input
              id="upload"
              ref="upload"
              type="file"
              style="display: none"
              multiple
              :accept="fileUploadAllowExtensions"
              @change="onFileUploadChange"
            />
          </el-row>
        </div>
        <div class="user-profile" v-if="!$device.Mobile" v-show="showUserDetails">
          <el-row>
            <el-collapse-transition>
              <div>
                <UserDetails :selected-chat="selectedChat" :value="userDetails" />
              </div>
            </el-collapse-transition>
          </el-row>
        </div>
      </div>
      <div v-if="showInteractionSidebar" class="chat-sidebar-shell">
        <!-- Resizer Handle -->
        <div v-if="canResizeSidebar" class="resizer" @mousedown="startResizing"></div>

        <!-- Resizable Sidebar -->
        <div
          ref="sidebar"
          class="chat-interaction-sidebar"
          :style="sidebarStyle"
        >
          <el-tabs style="height: 100%">
            <el-tab-pane label="Tags">
              <Tags
                :selected-chat="selectedChat"
                :is-queue-tab="isQueueTab"
                :is-resolved-tab="isResolvedTab"
                :state-variables="stateVariables"
                :sidebar-width="sidebarWidth"
              />
            </el-tab-pane>
            <el-tab-pane label="Profile" style="max-height: 100%; overflow-y: auto">
              <UserDetails :selected-chat="selectedChat" :value="userDetails" />
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </el-main>

    <PreviewReply @updatePulseRowKey="updatePulseRowKey" />
    <div v-if="dataCollectionFormEnabled && !pendingOrAttendingStatus">
      <el-card
        class="livechatTextBoxSection"
        width="100%"
        shadow="never"
        body-style="border: 1px 0px"
      >
        <p class="has-text-dark" style="text-align: center">This chat has ended on user side</p>
      </el-card>
    </div>

    <!-- File upload dialog -->
    <el-dialog
      title="File Upload confirmation"
      :visible.sync="fileUploadDialogVisible"
      :modal="false"
    >
      <span>These are the files you selected:</span>
      <el-main style="max-height: 300px">
        <el-row type="flex">
          <div v-for="(value, index) in confirmationFiles" :key="index" style="padding: 10px">
            {{ index + 1 }}. {{ value }}
            <br />
            <img
              v-if="isImageFile(value)"
              :src="confirmationFilesPreview[index]"
              style="height: 150px; width: auto"
            />
            <i v-else class="el-icon-document" style="font-size: 40px; padding: 20px 50px" />
          </div>
        </el-row>
      </el-main>
      <div class="error-message" v-if="fileValidationErrors">
        <p v-for="error in fileValidationErrors" :key="error">
          <i style="margin-right: 5px" class="el-icon-warning-outline" />
          {{ error }}
        </p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="fileUploadDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="confirmFileUpload" :disabled="!!fileValidationErrors">
          Confirm
        </el-button>
      </span>
    </el-dialog>

    <v-style v-if="isIE">
      .message-bubble-left:before { color: #0b93f6!important; border-left: 20px solid
      transparent!important; bottom:2px!important; } .message-bubble-left:after {
      background:transparent !important; } .message-bubble-right:before { color: #e5e5ea!important;
      border-right: 20px solid transparent!important; bottom:2px!important; }
    </v-style>

    <el-drawer
      ref="toolDrawer"
      title="Livechat Tools"
      :visible.sync="toolDrawer"
      :modal="false"
      class="tool-drawer"
      custom-class="drawer-container"
      direction="rtl"
    >
      <ExternalToolView />
    </el-drawer>
    <el-drawer
      title="Cobrowsing Session"
      :with-header="true"
      :visible.sync="inCobrowseSession"
      :append-to-body="true"
      direction="ttb"
      :modal="false"
      :size="!cobrowseDrawerOpen ? '0%' : '100%'"
      :custom-class="!cobrowseDrawerOpen ? 'cobrowse-drawer-hide' : ''"
      :wrapperClosable="false"
      :close-on-press-escape="false"
      :before-close="minimizeOrCloseCobrowseDrawer"
      ref="cobrowseDrawer"
    >
      <iframe focus="true" :src="cobrowseFrameUrl" height="100%" width="100%" frameborder="0" />
    </el-drawer>
  </el-container>
</template>

<script>
/**
 * ChatInteractions/Index - Live chat conversation interface.
 *
 * @component
 * @description Main live chat interaction panel displaying messages, input controls, and chat actions.
 * Handles message sending/receiving, file uploads, chat lifecycle (join/leave/resolve), agent invitations,
 * escalations, co-browsing sessions, Zoom meetings, data collection forms, and chat exports.
 * Integrates with GraphQL subscriptions for real-time message updates. Supports multiple channels
 * (webchat, WhatsApp, Facebook, etc.) with channel-specific features.
 *
 * @prop {Array} chatsList - List of all chats for navigation
 * @prop {Object} selectedChat - Currently active chat object
 * @prop {String} selectedRowKey - Selected chat row key
 * @prop {Boolean} isLoading - Loading state indicator
 * @prop {Boolean} closed - Mobile view closed state
 *
 * @fires update:closed - Syncs mobile view closed state
 * @fires update-assigned - Emits when chat assignment changes
 *
 * @listens livechatSessionMessage - GraphQL subscription for new messages
 * @listens livechatSessionStatus - GraphQL subscription for chat status changes
 *
 * @todo REFACTOR (Priority: CRITICAL): This file has 2818 lines.
 *   Recommended split:
 *   - components/ChatInteractions/MessageList.vue (~600 lines) - Message display, scroll, pagination
 *   - components/ChatInteractions/ChatHeader.vue (~400 lines) - Header with actions, buttons, timelines
 *   - components/ChatInteractions/ChatInput.vue (~350 lines) - Message input, file upload, quick replies
 *   - components/ChatInteractions/ChatActions.vue (~300 lines) - Join, leave, resolve, escalate logic
 *   - components/ChatInteractions/ChatExport.vue (~250 lines) - Excel, PDF, text export logic
 *   - components/ChatInteractions/CobrowsingPanel.vue (~200 lines) - Co-browsing iframe and controls
 *   - Store actions for chat operations (sendMessage, joinChat, etc.)
 *   - Composables for message handling, file uploads, subscriptions
 *
 * @example
 * <ChatInteractions
 *   :selected-chat="chat"
 *   :chats-list="chats"
 *   @update-assigned="handleUpdate"
 * />
 */
import FormEnabledStatus from "@/components/ChatInteractions/FormEnabledStatus/Index";
import VisitorProfile from "@/components/LiveChat/VisitorProfile";
import Message from "./Message/Index";
import FromNow from "@/components/LiveChat/DynamicFromNow";
import EscalationTimeline from "@/components/ChatInteractions/Timelines/EscalationTimeline";
import UserQueryTopicTimeline from "@/components/ChatInteractions/Timelines/UserQueryTopicTimeline";
import EscalateChat from "./EscalateChat/Index";
import InviteAgent from "./InviteAgent/Index";
import ZoomMeetingButton from "./ZoomMeetingButton/Index";
import PurgeBanner from "./PurgeBanner/Index";
import Tags from "./Tags/Index";
import { listenGraphQLWebsocket, GRAPHQL_WEBSOCKET_STATE } from "@/store/api";
import LocalStorageManager from "@/localStorageManager";
import ExternalToolView from "./ExternalToolView";
import { prepareExcelText } from "@/helperMethods/livechat/export";
import { isFunction } from "@/helperMethods/util";
import {
  getAvatar,
  getLastSentMessageTimeStamp,
  isIEOrEdge,
  isMessageByUser,
} from "@/helperMethods/livechat/util";
import { validateFiles } from "@/helpers/validateFiles";
import {
  isNotificationEnable,
  requestPushNotificationPermission,
} from "@/helperMethods/pushNotification";
import { getChatName } from "@/filters";

import { mapGetters } from "vuex";
import _ from "lodash";
import QuickReplies from "./QuickReplies";
import HelperActions from "./HelperActions";
import UserDetails from "./UserDetails/Index";
import XLSX from "xlsx";
import Vue from "vue";
import { defaultOptionsXSS } from "@/helpers/xssHelper";
import { filterXSS } from "xss";
import { SYSTEM_MESSAGE } from "@/lib/viewConstants";
import notificationUnreadMessage from "../../../static/mixkit-happy-bell-alert-601.wav";
import FileSaver from "file-saver";
import jsPDF from "jspdf";
import { robotoRegular } from "@/helpers/jsPDFFonts";
import PreviewReply from "./PreviewReply";
import moment from "moment";

Vue.component("v-style", {
  render: function (createElement) {
    return createElement("style", this.$slots.default);
  },
});

export default {
  name: "ChatsInteractions",
  components: {
    Message,
    VisitorProfile,
    FormEnabledStatus,
    FromNow,
    EscalateChat,
    InviteAgent,
    ZoomMeetingButton,
    Tags,
    QuickReplies,
    HelperActions,
    PurgeBanner,
    UserDetails,
    EscalationTimeline,
    UserQueryTopicTimeline,
    PreviewReply,
    ExternalToolView,
  },
  props: {
    chatsList: Array,
    selectedChat: Object,
    // selectedIndex: Number,
    selectedRowKey: String,
    isLoading: Boolean,

    closed: Boolean,
  },
  data() {
    const currentViewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
    const hideTagsPanelByDefault = _.get(
      this,
      "$store.state.modules.handover.hideTagsPanelByDefault",
      false
    );

    return {
      userDetails: {
        name: "",
        alias: "",
        mobileNo: "",
        email: "",
        comments: "",
      },

      showUserDetails: false,
      isIE: false,
      formShowing: false,
      selectedTransferAgentEmail: "",
      inviteAgentDialogVisible: false,
      fileUploadDialogVisible: false,
      toDepartment: "",
      transferMemoInput: "",
      isUploadable: false,
      confirmationFiles: [],
      confirmationFilesPreview: [],
      MAX_TABLET_WIDTH: 1024,

      typingIndicatorStatus: false,

      replyMessage: "",

      showMoreConversationButton: true,
      showNewerConversationButton: false,

      isAgentTyping: false,
      busyStates: {}, // Add this to track busy states for each chat

      canAgentReceiveNewMessage: true,
      fileValidationErrors: null,
      isNotificationEnable: false,
      scanningVirus: false,

      // co browse
      cobrowseFrameUrl: "",
      inCobrowseSession: false,
      cobrowseDrawerOpen: false,
      isUserOnSite: true,
      chatSessions: [],

      exportType: ".txt",
      pulseRowKey: "",
      pulseTimeout: null,
      skipNextInteractionAutoScroll: false,
      sidebarWidth: 300,
      currentViewportWidth,
      sidebarVisible: currentViewportWidth > 1366 && !hideTagsPanelByDefault,
      isResizing: false,
      startX: 0,
      initialWidth: 0,

      toolDrawer: false,
    };
  },
  computed: {
    ...mapGetters([
      "dataCollectionFormEnabled",
      "showAdvanced",
      "activeTabName",
      "isQueueTab",
      "isResolvedTab",
      "isMonitorTab",
      "selectedChatId",
      "isAgentOnline",
      "isRoutingAssignmentMode",
      "isRoutingBroadcastMode",
      "getAgentSOEId",
      "getAgentName",
      "getTypingIndicator",
      "replyObj",
      "monitorChatFilters"
    ]),
    fileUploadAllowExtensions() {
      return (
        this.$store?.state?.modules?.core?.fileUploadAllowExtensions ||
        ".html, .csv, .pdf, image/*, .json, .index, .doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .mp4, .webm"
      );
    },
    differentDepartment() {
      const userDepartments = _.get(this.$store, "state.profile.app_metadata.departments", []);
      const userDepartmentsWithoutGeneral = _.filter(userDepartments, (dept) => dept !== "general");
      const chatDepartment = _.get(this.selectedChat, "stateVariables.userQueryTopic");
      if (
        !chatDepartment ||
        _.isEmpty(userDepartmentsWithoutGeneral) ||
        !this.isHandoverRoutingByDepartmentEnabled
      ) {
        return false;
      }

      return !userDepartmentsWithoutGeneral.includes(chatDepartment);
    },

    isHelperActionsEnabled() {
      return _.get(this, "$store.state.modules.handover.liveChatHelperActions.enabled", false);
    },

    /**
     * @description Is handover by department enabled
     * @return {boolean}
     */
    isHandoverRoutingByDepartmentEnabled() {
      return _.get(this.$store.state, "modules.handover.handoverRouting.byDepartment", false);
    },

    /**
     * @description Has more than 1 agent in chat
     * @return {boolean}
     */
    hasMoreThanOneAgentInChat() {
      const agentsCounter = {};
      const chatInteractions = _.get(this.selectedChat, "interactions", []);
      chatInteractions.forEach((chat) => {
        if (chat.type !== "agent") {
          return;
        }
        const agent = _.get(chat, "meta.email");
        if (!agent) {
          return;
        }
        if (!agentsCounter[agent]) {
          agentsCounter[agent] = 1;
        }
        agentsCounter[agent]++;
      });

      const numberOfAgentsFromInteractions = Object.keys(agentsCounter).length;
      const numberOfAttendedAgents = _.get(this.selectedChat, "agents", []).length;

      return numberOfAgentsFromInteractions > 1 || numberOfAttendedAgents > 1;
    },

    /**
     * @description Patched state variables
     * @return {any}
     */
    stateVariables() {
      const exluded = [
        "channel",
        "source",
        "session_history",
        "RowKey",
        "PartitionKey",
        "user_id",
        "source_wa_id",
        "isPreparingUserToQueue",
      ];
      if (
        this.selectedChat?.stateVariables?.department === "00000000-0000-0000-0000-000000000000"
      ) {
        exluded.push("department");
      }
      return _.chain(this.selectedChat)
        .get("stateVariables", {})
        .omit(exluded)
        .omitBy((variable) => _.isObject(variable) || _.isPlainObject(variable))
        .value();
    },

    selectedChatformData() {
      if (_.isEmpty(this.selectedChat)) {
        return null;
      }
      return Object.assign({}, this.selectedChat.formData, {
        agentName: this.getAgentName,
        agentSoeId: this.getAgentSOEId,
      });
    },
    visitorProfileStyle() {
      if (this.$device.Mobile) {
        return this.formShowing ? { width: "100%" } : { width: "0px" };
      }
      return this.formShowing ? { width: "500px" } : { width: "0px" };
    },
    mainChatStyle() {
      return this.formShowing ? { marginRight: "500px" } : { marginRight: "0" };
    },

    agentIsNotOnline() {
      return !this.isAgentOnline;
    },
    pendingOrAttendingStatus() {
      const selectChatStatus = this.selectedChat.status;
      const chatNotAttendedTo = selectChatStatus === "attending" || selectChatStatus === "pending";
      return chatNotAttendedTo;
    },

    collapseIcon() {
      let icon = "el-icon-arrow-left";
      if (this.formShowing) {
        icon = "el-icon-arrow-right";
      }
      return icon;
    },
    replyFileMessage: {
      get: function () {
        const isObjectReplyFile = typeof _.get(this, "selectedChat.replyFile") === "object";
        if (isObjectReplyFile) {
          return this.selectedChat.replyFile;
        } else {
          return {};
        }
      },
      set: function (newValue) {
        if (this.selectedChat) {
          Vue.set(this.selectedChat, "replyFile", newValue);
        }
      },
    },
    loadingText() {
      if (this.scanningVirus) {
        return "Scanning files for viruses...";
      }
      return !this.busyStates[this.selectedChat?.RowKey]?.uploading
        ? "Loading selected chat..."
        : "Uploading files ...";
    },
    selectedChatInteractions() {
      const interactions = _.chain(this.selectedChat)
        .get("interactions", [])
        .uniqBy((interaction) => interaction.RowKey)
        .orderBy(["last_modified"], ["asc"])
        .value();
      return interactions;
    },
    lastSentMessageTimeStamp() {
      const lastTimestamp = getLastSentMessageTimeStamp(
        this.selectedChat,
        this.selectedChatInteractions
      );
      return lastTimestamp;
    },
    isInChat() {
      if (this.isResolvedTab || this.isMonitorTab) {
        return false;
      } else {
        const agents = _.get(this, "selectedChat.agents", []).map((email) => email.toLowerCase());
        return agents.includes(this.$store.state.profile.email?.toLowerCase());
      }
    },

    hasMoreThanOneAgents() {
      const agents = this.selectedChat.agents || [];
      const hasMoreThanOneAgents = agents.length > 1;
      return hasMoreThanOneAgents;
    },

    shortcuts() {
      return _.map(this.$store.state.nodes.content, (content, nodeId) => {
        const appSource = _.chain(content)
          .get("conditions", [])
          .filter((o) => o.property === "appSource")
          .map((o) => o.value)
          .value();
        const departments = _.get(content, "department", ["general"]);
        return {
          name: nodeId,
          message: content.content.text,
          options: "",
          tags: [],
          appSource,
          departments,
        };
      });
    },

    isCobrowsingEnabled() {
      const cobrowseEnabled = _.get(this, "$store.state.modules.handover.cobrowse.enabled");
      const cobrowseApiKey = _.get(this, "$store.state.modules.handover.cobrowse.apiKey");
      return cobrowseEnabled && cobrowseApiKey;
    },
    channelName() {
      const channel =
        _.get(this.selectedChat, "stateVariables.channel", this.selectedChat.channel) || "unknown";
      return channel;
    },
    filteredXSSMessage() {
      const textBeforeXSSFilter = this.replyMessage.trim();
      return filterXSS(textBeforeXSSFilter, defaultOptionsXSS);
    },
    isLivechatQuickRepliesEnabled() {
      const result = _.get(this, "$store.state.modules.system.livechatQuickReplies.enabled", false);
      return result;
    },
    isDisableAutoSwitchToResolvedTab() {
      return _.get(this.$store.state, "modules.handover.disableSwitchToResolveTab", false);
    },
    // Hide conversation history if user is at queue tab
    // AND selected chat is not assigned to the current agent
    // AND hide conversation history setting is enabled
    // AND chat is in the specified departments
    shouldHideConversationHistory() {
      if (!this.isQueueTab || this.isInChat) return false;

      const hideHistorySettings = this.$store.state.modules?.handover?.hideConversationHistory;

      const isHideSettingEnabled = _.get(hideHistorySettings, "enabled", false);
      const hideSettingDepartments = _.get(hideHistorySettings, "departments", []).map((dept) =>
        dept.toLowerCase()
      );
      const chatDepartment = _.get(
        this.selectedChat,
        "stateVariables.userQueryTopic",
        ""
      ).toLowerCase();

      const isChatInHideSettingDepartments =
        isHideSettingEnabled &&
        (_.isEmpty(hideSettingDepartments) || _.includes(hideSettingDepartments, chatDepartment));

      return isChatInHideSettingDepartments;
    },
    sidebarStyle() {
      return {
        width: this.resolvedSidebarWidth + "px",
        borderLeft: "1px solid #e4e7ed",
        padding: "10px",
        height: "100%",
        overflow: "hidden",
        flexShrink: 0,
      };
    },
    showInteractionSidebar() {
      return !this.$device.Mobile && this.sidebarVisible;
    },
    canResizeSidebar() {
      return this.showInteractionSidebar && this.currentViewportWidth >= 1440;
    },
    resolvedSidebarWidth() {
      if (this.currentViewportWidth >= 1440) {
        return this.sidebarWidth;
      }

      return 320;
    },
    sidebarToggleTooltip() {
      return this.sidebarVisible ? "Hide tags and profile panel" : "Show tags and profile panel";
    },
    shouldShowMoreActions() {
      return (
        (this.isQueueTab && this.isHandoverRoutingByDepartmentEnabled) ||
        (this.isQueueTab &&
          this.isInChat &&
          this.isCobrowsingEnabled &&
          this.channelName === "webchat") ||
        (this.isQueueTab && this.dataCollectionFormEnabled) ||
        (this.isQueueTab && this.isInChat && this.$store.state.modules.zoom.enabled) ||
        this.dataCollectionFormEnabled ||
        !this.isNotificationEnable
      );
    },

    handoverExtToolOptions() {
      return _.get(this, "$store.state.modules.handover.externalTool", {});
    },
    shouldShowExternalTool() {
      return this.handoverExtToolOptions?.enabled && this.handoverExtToolOptions?.tools?.length > 0;
    } 
  },
  methods: {
    getChatName,
    getPulseClass(msgRowKey) {
      if (msgRowKey === this.pulseRowKey) {
        return "message-border-pulse";
      }
      return "";
    },
    async updatePulseRowKey(RowKey) {
      if (this.pulseTimeout) {
        clearTimeout(this.pulseTimeout);
        this.pulseTimeout = null;
      }
      const foundInteraction = this.selectedChatInteractions.find(
        (interaction) => interaction.RowKey === RowKey
      );
      if (!foundInteraction) {
        const NON_VISIBLE_MESSAGE =
          "The referenced message is not currently visible. Try loading more conversation history to view it.";
        const NON_AVAILABLE_MESSAGE =
          "The referenced message might not available to view for quite older messages.";
        const INTERNAL_ERROR =
          "An error occurred while retrieving the referenced message. Please try again later.";
        try {
          const { isExist } = await this.$store.dispatch("CHECK_INTERACTION_IN_LIVECHATSESSION", {
            RowKey,
          });
          if (isExist) {
            this.$message({
              type: "warning",
              message: NON_VISIBLE_MESSAGE,
            });
          } else {
            this.$message({
              type: "warning",
              message: NON_AVAILABLE_MESSAGE,
            });
          }
        } catch (err) {
          this.$message({
            type: "warning",
            message: INTERNAL_ERROR,
          });
        }
        return;
      }

      // if the context within interactions window
      this.pulseRowKey = RowKey;
      if (this.$refs?.[`message-${RowKey}`]?.[0]) {
        this.$refs[`message-${RowKey}`][0].scrollIntoView({
          behavior: "smooth",
        });
      }
      this.pulseTimeout = setTimeout(() => {
        this.pulseRowKey = "";
      }, 1000);
    },
    enableNotification() {
      requestPushNotificationPermission(
        () => {
          this.isNotificationEnable = true;
          this.$message({
            message: "Browser notification was enabled",
            type: "success",
          });
        },
        () => {
          this.isNotificationEnable = false;
          this.$message({
            message: "Enable browser notification was not successful.",
            type: "warning",
          });
        }
      );
    },
    handleShortcutSelection(selection) {
      const message = Array.isArray(selection.message) ? selection.message[0] : selection.message;
      this.replyMessage = message;
    },

    getShortcuts(query, callback) {
      if (!query || query.charAt(0) !== "/") {
        callback([]);
        return;
      }

      query = query.substr(1);
      const numberToDisplay = 10;
      const queryLowerCase = query.toLowerCase();
      // get app source for current active chat
      const activeChatAppSourceFromUserQuery = _.get(
        this.selectedChat,
        "stateVariables.userQueryTopic"
      );
      const activeChatAppSourceFromTags = _.chain(this.selectedChat)
        .get("tags")
        .find((tag) => tag.includes("appSource:"))
        .split(":")
        .get("[1]", null)
        .value();
      const activeChatAppSource = _.chain([
        activeChatAppSourceFromUserQuery,
        activeChatAppSourceFromTags,
      ])
        .compact()
        .uniq()
        .value();

      // get suggestion filter settings and user department for filtering purpose
      const checkDepartment = _.get(
        this.$store.state.modules,
        "system.livechatReplySuggestionsFilter",
        false
      );
      const activeChatDepartments = this.$store.getters.userDepartment.length
        ? this.$store.getters.userDepartment
        : ["general"];

      // filter available shortcuts to fit in required condition
      const shortcuts = _.chain(this.shortcuts)
        .filter((shortcut) => {
          const message = _.flatten([shortcut.message])[0];
          const shortcutAppSource = shortcut.appSource;
          const shortcutDepartments = _.uniq([...shortcut.departments, "general"]);

          const appSourceIsMatch = !(
            activeChatAppSource.length &&
            shortcutAppSource.length &&
            _.chain(shortcutAppSource).intersection(activeChatAppSource).isEmpty().value()
          );
          const departmentIsMatch = checkDepartment
            ? !_.chain(shortcutDepartments).intersection(activeChatDepartments).isEmpty().value() ||
              (shortcutDepartments.length === 1 && shortcutDepartments[0] === "general")
            : true;

          return (
            appSourceIsMatch &&
            departmentIsMatch &&
            `/${shortcut.name} ${message}`.toLowerCase().includes(queryLowerCase)
          );
        })
        .take(numberToDisplay)
        .value();

      // pass the return value in callback function
      // ref: https://element.ele.me/#/en-US/component/input#autocomplete
      callback(shortcuts);
    },
    getAvatar,
    isIEOrEdge,
    isMessageByUser,
    resetFormShowingAndButtons() {
      this.formShowing = false;
      const flashingButton = document.getElementById("flashing-button");

      if (flashingButton) {
        flashingButton.className.replace(/\bnotification-flashing\b/g, "");
      }
    },
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    handleViewportResize() {
      this.currentViewportWidth = window.innerWidth;

      if (window.innerWidth <= 1024) {
        this.sidebarVisible = false;
      }

      if (!this.canResizeSidebar) {
        this.stopResizing();
      }
    },
    toggleFormData() {
      const formNotShowing = !this.formShowing;

      this.formShowing = formNotShowing;
    },
    handleSendEvent(event) {
      event.preventDefault();
      this.reply();
    },
    isDisconnectMessage(message) {
      return (
        message.error &&
        message.data.content &&
        message.data.content.length === 1 &&
        message.data.content[0].text
      );
    },
    isReconnectMessage(message) {
      return (
        message.data.content &&
        message.data.content.length === 1 &&
        message.data.content[0].text &&
        message.data.content[0].text.toLowerCase() === SYSTEM_MESSAGE.connected
      );
    },
    formatMessageTimestamp(timestamp) {
      // Format the timestamp to match the format used in Bubble.vue
      const result = moment(timestamp).local().calendar(moment(), {
        sameDay: "[Today at] h:mm A",
        nextDay: "[Tomorrow at] h:mm A",
        nextWeek: "[Next] dddd [at] h:mm A",
        lastDay: "[Yesterday at] h:mm A",
        lastWeek: "DD-MMM-YYYY [at] h:mm A",
        sameElse: "DD-MMM-YYYY [at] h:mm A",
      });
      return result;
    },
    isImageFile(url) {
      const imageRegex = new RegExp("(jpg|jpeg)|(png)", "gi");
      return imageRegex.test(url);
    },
    async confirmFileUpload() {
      const chat = this.selectedChat;
      this.fileUploadDialogVisible = false;
      this.updateUploadStatus(true);
      const files = this.event.target.files;

      if (!files || files.length === 0) {
        this.updateUploadStatus(false);
        return;
      }

      // Step 1: Scan each file for viruses
      this.scanningVirus = true;
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formData = new FormData();
          formData.append("file", file);

          const scanResult = await this.$rest("post", "miniapp_virus_scan", formData);
          const unexpectedResult = scanResult.is_infected === undefined && scanResult.viruses === undefined;
          const isInfected = scanResult.is_infected;
          const virusDetected = !_.isEmpty(scanResult.viruses);

          if (unexpectedResult || isInfected || virusDetected) {
            const message =
              isInfected || virusDetected
                ? `File ${file.name} contains suspicious malware and was rejected.`
                : "Failed to process virus scan. Please try again or contact support.";

            this.$notify.error({
              title: "Security Alert",
              position: "bottom-right",
              message,
            });
            this.$refs.upload.value = [];
            this.updateUploadStatus(false);
            return;
          }
        }
      } catch (error) {
        const defaultMessage = "An error occurred while scanning file for viruses.";
        const errorMessage = _.get(error, "response.data.message", defaultMessage);

        this.$notify.error({
          title: "Scan Error",
          position: "bottom-right",
          message: errorMessage || defaultMessage,
        });
        this.$refs.upload.value = [];
        this.updateUploadStatus(false);
        return;
      } finally {
        this.scanningVirus = false;
      }

      // Step 2: If all files are clean, proceed with upload
      const fileNames = [];
      let formData = new FormData();
      const isIEOrEdge = this.isIEOrEdge();
      for (let i = 0; i < files.length; i++) {
        fileNames.push(files[i].name);
        if (isIEOrEdge) {
          formData.append(`files[${i}]`, files[i]);
        }
      }

      this.$store
        .dispatch("REQUEST_SAS", { fileNames })
        .then((sasUrls) => {
          this.$store
            .dispatch("UPLOAD_FILE", {
              sasUrls,
              formData,
              files,
              isIEOrEdge,
              rowKey: this.selectedChat.user_id,
            })
            .then(async (response) => {
              const imageUrls = [];
              const nonImageUrls = [];
              sasUrls.map((url, index) => {
                const imageRegex = new RegExp("(jpg|jpeg)|(png)", "gi");
                const newUrl = url.slice(0, url.indexOf("?"));

                if (imageRegex.test(newUrl)) {
                  imageUrls.push(newUrl);
                } else {
                  nonImageUrls.push(newUrl);
                }
                return;
              });

              const responseFromCeph = _.isEmpty(sasUrls);
              if (responseFromCeph && response) {
                response.map((url, index) => {
                  const imageRegex = new RegExp("(jpg|jpeg)|(png)", "gi");
                  const newUrl = url.slice(url.lastIndexOf("/"), url.length);
                  if (imageRegex.test(newUrl)) {
                    imageUrls.push(url);
                  } else {
                    nonImageUrls.push(url);
                  }
                  return;
                });
              }

              if (imageUrls.length > 0) {
                this.replyFileMessage = { images: imageUrls };
                await this.reply(chat);
              }

              if (nonImageUrls.length > 0) {
                this.replyFileMessage = { files: nonImageUrls };
                await this.reply(chat);
              }

              this.updateUploadStatus(false);
              this.$refs.upload.value = [];

              // In IE 11, the dialog will reappear
              this.fileUploadDialogVisible = false;
            })
            .catch((err) => {
              this.$refs.upload.value = [];
              this.updateUploadStatus(false);
              this.$notify.error({
                title: "Error",
                message: "An error occurred while uploading file!",
                position: "bottom-right",
              });
            });
        })
        .catch((err) => {
          this.$refs.upload.value = [];
          this.updateUploadStatus(false);
          this.$notify.error({
            title: "Error",
            message: "An error occurred while uploading file!",
            position: "bottom-right",
          });
        });
    },
    isFileUploadable() {
      const isFileUploadEnabled = this.$store.state.modules.webchat.fileUpload;

      // FIXME: WILL BE DEPRECATED @DHONI
      // const blob = new Blob([JSON.stringify({ hello: "world" })], {
      //   type: "application/json",
      // });

      // const testFile = !this.isIEOrEdge()
      //   ? new File([blob], "test_cors.json")
      //   : blob;

      if (!isFileUploadEnabled) {
        this.isUploadable = false;

        return;
      }

      this.isUploadable = true;
      // FIXME: no need to verify cors allow origin
      // this.$store.dispatch("GET_STORAGE_HOST").then((data) => {
      //   const url = new URL(data.primary).origin;

      //   this.$store
      //     .dispatch("VERIFY_CORS_SAS_URL", { url })
      //     .then((response) => {
      //       this.isUploadable = true;
      //     })
      //     .catch((error) => {
      //       this.isUploadable = false;
      //     });

      // FIXME: @Dhoni To be deprecated!
      // // Upload to temp blob
      // this.$store
      //   .dispatch("UPLOAD_FILE", {
      //     sasUrls,
      //     event: "file_upload",
      //     files: [testFile],
      //     text: "File Upload",
      //   })
      //   .then(async (response) => {
      //     // display file as message to client
      //     this.isUploadable = true;
      //   })
      //   .catch((error) => {
      //     this.isUploadable = false;
      //   });
      // });
    },

    updateUploadStatus(uploadStatus = false, chatId = this.selectedChat?.RowKey) {
      this.setBusyState(chatId, "uploading", uploadStatus);
    },

    generateExcel(chat, tabName = "") {
      const keysToBeExcluded = [
        "livechat_data",
        "session_history",
        "headers",
        "temp",
        "private_variables",
        "ip",
        "interactions",
      ];

      const additionalKeysToBeExcluded =
        _.get(this.$store, "state.modules.core.livechatExportExcludedFields") || [];
      keysToBeExcluded.push(...additionalKeysToBeExcluded);

      const ws_data = prepareExcelText(chat, keysToBeExcluded, additionalKeysToBeExcluded);

      if (this.exportType === "xlsx") {
        let wb = XLSX.utils.book_new();
        let ws = XLSX.utils.aoa_to_sheet(ws_data);

        const sheetName = _.get(chat, "RowKey", "").substring(0, 15);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        const filename = this.$exportFilename(`chat_conversations_${tabName}`, "xlsx");
        XLSX.writeFile(wb, filename, {});
      } else {
        let text = "";
        for (let index = 0; index < ws_data.length; index++) {
          const element = ws_data[index];
          if (element.length > 0) {
            text += element.join(" ") + "\n";
          } else {
            text += "\n";
          }
        }

        if (this.exportType === "pdf") {
          const pdf = new jsPDF();
          const margin = 15;
          const pageHeight = pdf.internal.pageSize.getHeight() - 2 * margin;

          // Set the font
          pdf.addFileToVFS("Roboto-Regular.ttf", robotoRegular);
          pdf.addFont("Roboto-Regular.ttf", "Roboto-Regular", "normal");
          pdf.setFont("Roboto-Regular");
          pdf.setFontSize(12);

          // Split text into lines to fit the page width
          const textLines = pdf.splitTextToSize(
            text,
            pdf.internal.pageSize.getWidth() - 2 * margin
          );

          let cursorY = margin;

          textLines.forEach((line) => {
            // Check if adding the line will exceed the page height
            if (cursorY + pdf.getTextDimensions(line).h > pageHeight) {
              // Add a new page if necessary
              pdf.addPage();
              cursorY = margin;
            }

            pdf.text(margin, cursorY, line);
            cursorY += pdf.getTextDimensions(line).h + 2; // Add a small gap between lines
          });

          const filename = this.$exportFilename(`chat_conversations_${tabName}`, "pdf");
          pdf.save(filename);
        } else {
          const file = new Blob([text], {
            type: "text/plain;charset=utf-8",
          });
          const filename = this.$exportFilename(`chat_conversations_${tabName}`, "txt");
          FileSaver.saveAs(file, filename);
        }
      }

      this.$notify.success({
        title: "Download Success",
        message: "Successfully downloaded selected chat",
        position: "bottom-right",
      });
    },
    handleExportChat(type) {
      this.exportType = type;
      this.exportChat();
    },
    exportChat() {
      const isQueueTab = this.isQueueTab;
      const isResolvedTab = this.isResolvedTab;
      const isMonitorTab = this.isMonitorTab;
      const isAbandonedTab = this.activeTabName === "abandoned";

      if (isMonitorTab) {
        this.exportMonitorChat();
        return;
      }

      if (isResolvedTab || isAbandonedTab) {
        this.exportResolvedChat();
        return;
      }

      if (isQueueTab) {
        this.exportQueueChat();
        return;
      }
    },
    exportQueueChat() {
      const currentChat = _.cloneDeep(this.selectedChat);
      const actionToBeCalled = "GET_SESSION_BY_USER_ID_AND_PARTITION_KEY";
      const paramsToBePassed = {
        userId: currentChat.user_id,
        sessionPartitionKey: currentChat.PartitionKey,
        isResolved: false,
      };

      this.$store
        .dispatch(actionToBeCalled, paramsToBePassed)
        .then((chat) => {
          this.generateExcel(chat, "queue");
        })
        .catch((err) => {
          let message = "Encountered error exporting chat";
          if (this.showAdvanced) {
            message = err.message;
          }
          this.$notify.error({
            title: "Error",
            message,
            position: "bottom-right",
          });
        });
    },
    exportResolvedChat() {
      const currentChat = _.cloneDeep(this.selectedChat);
      const isResolvedTab = this.isResolvedTab;
      if (isResolvedTab) {
        this.generateExcel(currentChat, "resolved");
      } else {
        this.generateExcel(currentChat, "abandoned");
      }
    },
    exportMonitorChat() {
      const currentChat = _.cloneDeep(this.selectedChat);

      // monitor export will only fetch more livechat interactions to be exported
      this.$store
        .dispatch("FETCH_MORE_MONITOR_LIVECHAT_INTERACTION", {
          partitionKey: currentChat.PartitionKey,
          userId: currentChat.RowKey,
          limit: 1000,
          offset: 0,
        })
        .then((interactions) => {
          const currentInteractions = currentChat.interactions;
          const newInteractions = _.concat(currentInteractions, interactions);
          const newChat = Object.assign({}, currentChat, {
            interactions: newInteractions,
          });

          this.generateExcel(newChat, "monitor");
        })
        .catch((err) => {
          let message = "Encountered error exporting chat";
          if (this.showAdvanced) {
            message = err.message;
          }
          this.$notify.error({
            title: "Error",
            message,
            position: "bottom-right",
          });
        });
    },
    joinLivechat: _.throttle(
      function (chatToJoin) {
        this.setBusyState(chatToJoin?.RowKey, "isJoiningChat", true);
        const agentId = this.$store.state.profile.email;
        this.$store
          .dispatch("CHECK_LIVECHAT_AVAILABILITY", {
            chat: chatToJoin,
            agentId,
          })
          .then((response) => {
            if (!response.success) {
              this.$notify.warning({
                title: "Attention",
                message: response.reason,
                position: "bottom-right",
              });

              this.selectedChat = null;
              return;
            }
            return this.dispatchJoinLivechat(chatToJoin);
          })
          .catch((err) => {
            this.$notify.error({
              title: "Error",
              message: "Encountered error checking for chat's availability",
              position: "bottom-right",
            });
          })
          .finally(() => {
            this.setBusyState(chatToJoin?.RowKey, "isJoiningChat", false);
          });
      },
      1000,
      { leading: true }
    ),
    dispatchJoinLivechat(chatToJoin) {
      const agentId = this.$store.state.profile.email;
      return this.$store
        .dispatch("JOIN_LIVECHAT", {
          chat: chatToJoin,
          agentId,
        })
        .then((response) => {
          if (!response.success) {
            this.$notify.error({
              title: "Error",
              message: response.reason,
              position: "bottom-right",
            });
          }
        })
        .catch((err) => {
          this.$notify.error({
            title: "Error",
            message: "Encountered error joining chat",
            position: "bottom-right",
          });
        });
    },
    leaveLivechat: _.throttle(
      function (chatToJoin) {
        this.setBusyState(chatToJoin?.RowKey, "isLeavingChat", true);
        const agentId = this.$store.state.profile.email;
        this.$store
          .dispatch("CHECK_RESOLVED_LIVECHAT", {
            chat: chatToJoin,
            agentId,
          })
          .then((response) => {
            if (!response.success) {
              this.$notify.error({
                title: "Error",
                message: response.reason,
                position: "bottom-right",
              });

              this.selectedChat = null;
              return;
            }

            return this.dispatchLeaveLivechat(chatToJoin);
          })
          .catch((err) => {
            this.$notify.error({
              title: "Error",
              message: "Encountered error checking resolved livechat",
              position: "bottom-right",
            });
          })
          .finally(() => {
            this.setBusyState(chatToJoin?.RowKey, "isLeavingChat", false);
          });
      },
      1000,
      { leading: true }
    ),
    dispatchLeaveLivechat(chatToJoin) {
      const agentId = this.$store.state.profile.email;
      return this.$store
        .dispatch("LEAVE_LIVECHAT", {
          chat: chatToJoin,
          agentId,
        })
        .then((chat) => {
          if (chat) {
            this.backToList();
          }
        });
    },
    backToList() {
      this.$emit("update:closed", false);
    },
    setResolve(_e, chatId = this.selectedChat?.RowKey) {
      if (!this.busyStates[chatId]?.isResolvingChat) {
        this.setBusyState(chatId, "isResolvingChat", true);
        const chat = this.selectedChat; //required because selectedChat cannot be used for PERSIST_LIVECHAT after RESOLVE_LIVECHAT
        const resolveChatPayload = {
          sessionPartitionKey: _.get(chat, "PartitionKey", ""),
          userId: chat.user_id,
          sessionRowKey: chat.RowKey,
        };
        this.$store
          .dispatch("RESOLVE_LIVECHAT", resolveChatPayload)
          .then((isResolved) => {
            if (isResolved) {
              // handle this on QueueChatsTab so that if customer trigger the resolve agent will get notify.
              // this.$notify.success({
              //   title: "Success",
              //   message: "Resolved chat successfully",
              //   position: "bottom-right"
              // });
              this.$emit("update-assigned", "");
            } else {
              this.$notify.error({
                title: "Error",
                message: "Encountered error resolving chat",
                position: "bottom-right",
              });
            }
          })
          .catch((error) => {
            this.$notify.error({
              title: "Error",
              message: "Encountered error resolving chat",
              position: "bottom-right",
            });
          })
          .finally(() => {
            this.setBusyState(chatId, "isResolvingChat", false);
            if (!this.isDisableAutoSwitchToResolvedTab) {
              this.$store.commit("SET_ACTIVE_TAB_NAME", "resolved");
            }
          });
        // this.$emit("update:selectedIndex", 0);
        this.$emit("update:selectedRowKey", "");
        this.backToList();
      }
    },
    sendToQueue(chatId = this.selectedChat?.RowKey) {
      if (
        this.agentIsNotOnline ||
        this.isBusy(this.selectedChat?.RowKey) ||
        this.differentDepartment
      ) {
        this.$notify.warning({
          title: "Warning",
          message:
            "The chat is either being sent to the queue, transferred to a different department, or you're currently in offline status.",
          position: "bottom-right",
        });
        return;
      }
      this.setBusyState(chatId, "isJoiningChat", true);
      this.$store
        .dispatch("SEND_TO_LIVECHAT_QUEUE", {
          user: this.selectedChat,
          agentEmail: this.$store.state.profile.email,
        })
        .then((response) => {
          const { isCreated, latestSession } = response;
          if (isCreated) {
            this.$notify.success({
              title: "Success",
              message: "User has been sent to queue successfully",
              position: "bottom-right",
            });
          } else {
            const agents = _.get(latestSession, "agents", []);
            let warningMessage;
            if (_.isEmpty(agents)) {
              warningMessage = `User is already in queue. Go to Queue tab to talk to user.`;
            } else {
              const agentNames = _.join(agents, ", ");
              warningMessage = `User is already in queue, being attended by agents: ${agentNames}.`;
            }

            this.$notify.warning({
              title: "Warning",
              message: warningMessage,
              position: "bottom-right",
            });
          }
        })
        .catch((err) => {
          this.$message({
            type: "error",
            message: "Encountered error sending to livechat queue",
          });
        })
        .finally(() => {
          this.setBusyState(chatId, "isJoiningChat", false);
        });
    },
    // File upload related functions
    fileUpload() {
      this.$refs.upload.click();
    },
    onFileUploadChange(event) {
      this.fileUploadDialogVisible = true;

      this.event = event;
      const files = event.target.files;

      this.fileValidationErrors = null;
      const { isValid, errors } = validateFiles(files, this.fileUploadAllowExtensions);
      if (!isValid) {
        this.fileValidationErrors = errors;
      }

      this.confirmationFilesPreview = _.map(files, (item, index) => {
        return URL.createObjectURL(item);
      });

      this.confirmationFiles = _.map(files, (item, index) => {
        return item.name;
      });
    },
    async reply(chat = this.selectedChat) {
      const isEmptyMessage = this.filteredXSSMessage.length === 0;
      const hasImages = this.replyFileMessage.images;
      const hasFiles = this.replyFileMessage.files;
      const source = _.get(chat, "stateVariables.source", "webchat");
      const contextRowKey = _.get(this.replyObj, "id");
      const reply = {
        page_id: chat.PartitionKey,
        user_id: chat.user_id,
        isUser: false,
        isResolved: false,
        source,
        contextRowKey,
      };

      // Attach message payload
      if (hasImages) {
        reply.images = this.replyFileMessage.images;
      } else if (hasFiles) {
        reply.files = this.replyFileMessage.files;
      } else {
        if (isEmptyMessage) {
          this.$notify.warning({
            message: "You cannot send a blank message.",
            position: "bottom-right",
          });

          return;
        } else {
          reply.text = this.filteredXSSMessage;
        }
      }
      this.replyMessage = "";
      LocalStorageManager.removeLivechatDraft(
        this.$store.state.profile.email,
        this.selectedChat.user_id
      );
      this.replyFileMessage = {};
      this.$nextTick(this.scrollToBottom);

      return this.sendMessage(reply)
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.$notify.error({
            title: "Cannot send message",
            message:
              error?.message || "Please check the internet connection and refresh this page.",
            position: "bottom-right",
          });
        });
    },
    sendMessage: _.debounce(
      function (reply) {
        return this.$store.dispatch("SEND_MESSAGE", { reply });
      },
      500,
      { leading: true }
    ),
    getChatWidget() {
      return this.$refs.chatWidget || null;
    },
    scrollToBottom() {
      const chatWidget = this.getChatWidget();
      if (chatWidget) {
        this.$nextTick(() => {
          chatWidget.scrollTop = chatWidget.scrollHeight;
        });
      }
    },

    scrollToTop() {
      const chatWidget = this.getChatWidget();
      if (chatWidget) {
        chatWidget.scrollTop = 0;
      }
    },

    getFetchMorePayload(chatObject, refetchAll = false) {
      // Get the current size of the interactions, send it for pagination size
      const currentChatCount = _.chain(chatObject).get("interactions", []).size().value();
      const offset = refetchAll ? 0 : currentChatCount;
      const limit = refetchAll ? currentChatCount : 20;
      const userId = chatObject.RowKey;
      const partitionKey = chatObject.PartitionKey;

      // Fetch 20 at a time
      const fetchMorePayload = { userId, partitionKey, limit, offset };
      return fetchMorePayload;
    },

    // Show more chat messages
    showMoreConversation(chatObject, shouldScrollToBottom = false, refetchAll = false, fetchingNewer = false) {
      const fetchMorePayload = this.getFetchMorePayload(chatObject, refetchAll);

      const isQueueTab = this.activeTabName === "queue";
      const isResolvedTab = this.activeTabName === "resolved";
      const isAbandonedTab = this.activeTabName === "abandoned";
      const isMonitorTab = this.activeTabName === "monitor";

      if (isQueueTab && !this.shouldHideConversationHistory) {
        return this.fetchMoreQueueChatMessages(fetchMorePayload, shouldScrollToBottom, chatObject);
      } else if (isResolvedTab) {
        return this.fetchMoreResolvedChatMessages(
          fetchMorePayload,
          fetchingNewer ? false : shouldScrollToBottom,
          chatObject
        );
      } else if (isAbandonedTab) {
        return this.fetchMoreAbandonedChatMessages(
          fetchMorePayload,
          shouldScrollToBottom,
          chatObject
        );
      } else if (isMonitorTab) {
        if (fetchingNewer) {
          return this.fetchMoreNewerMonitorChatMessages(
            fetchMorePayload,
            chatObject
          );
        }
        return this.fetchMoreMonitorChatMessages(
          fetchMorePayload,
          shouldScrollToBottom,
          chatObject
        );
      }
    },
    fetchMoreQueueChatMessages(
      fetchMorePayload,
      shouldScrollToBottom = false,
      chatObject = this.selectedChat
    ) {
      this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", true);
      return this.$store
        .dispatch("FETCH_MORE_LIVECHAT_INTERACTION", fetchMorePayload)
        .then((newInteractions) => {
          const fetchedAll = newInteractions.length === 0;
          if (fetchedAll) {
            this.showMoreConversationButton = false;
          }

          const currentInteractions = chatObject.interactions;
          const updatedInteractions = _.concat(newInteractions, currentInteractions);
          const updatedLivechat = Object.assign({}, chatObject, {
            interactions: updatedInteractions,
          });
          this.$store.commit("UPDATE_QUEUE_CHAT", { updatedLivechat });
          if (!shouldScrollToBottom) {
            this.$nextTick(this.scrollToTop);
          } else {
            this.$nextTick(this.scrollToBottom);
          }
        })
        .catch((err) => {
          this.$notify.error({
            title: "Error",
            message: "Encountered error fetching queued chat history",
            position: "bottom-right",
          });
        })
        .finally(() => {
          this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", false);
        });
    },
    fetchMoreResolvedChatMessages(
      fetchMorePayload,
      shouldScrollToBottom = false,
      chatObject = this.selectedChat
    ) {
      this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", true);
      return this.$store
        .dispatch("FETCH_MORE_LIVECHAT_INTERACTION", fetchMorePayload)
        .then((newInteractions) => {
          const fetchedAll = newInteractions.length === 0;
          if (fetchedAll) {
            this.showMoreConversationButton = false;
          }

          const currentInteractions = chatObject.interactions;
          const updatedInteractions = _.concat(currentInteractions, newInteractions);
          const updatedLivechat = Object.assign({}, chatObject, {
            interactions: updatedInteractions,
          });
          this.skipNextInteractionAutoScroll = true;
          this.$store.commit("UPDATE_RESOLVED_CHAT", { updatedLivechat });
          if (!shouldScrollToBottom) {
            this.$nextTick(this.scrollToTop);
          } else {
            this.$nextTick(this.scrollToBottom);
          }
        })
        .catch((error) => {
          this.$notify.error({
            title: "Error",
            message: "Encountered error fetching resolved chat history",
            position: "bottom-right",
          });
        })
        .finally(() => {
          this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", false);
        });
    },
    fetchMoreAbandonedChatMessages(
      fetchMorePayload,
      shouldScrollToBottom = false,
      chatObject = this.selectedChat
    ) {
      this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", true);
      return this.$store
        .dispatch("FETCH_MORE_LIVECHAT_INTERACTION", fetchMorePayload)
        .then((newInteractions) => {
          const fetchedAll = newInteractions.length === 0;
          if (fetchedAll) {
            this.showMoreConversationButton = false;
          }

          const currentInteractions = chatObject.interactions;
          const updatedInteractions = _.concat(currentInteractions, newInteractions);
          const updatedLivechat = Object.assign({}, chatObject, {
            interactions: updatedInteractions,
          });
          this.$store.commit("UPDATE_ABANDONED_CHAT", { updatedLivechat });
          if (!shouldScrollToBottom) {
            this.$nextTick(this.scrollToTop);
          } else {
            this.$nextTick(this.scrollToBottom);
          }
        })
        .catch((error) => {
          this.$notify.error({
            title: "Error",
            message: "Encountered error fetching abandoned chat history",
            position: "bottom-right",
          });
        })
        .finally(() => {
          this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", false);
        });
    },
    fetchMoreMonitorChatMessages(
      fetchMorePayload,
      shouldScrollToBottom = false,
      chatObject = this.selectedChat
    ) {
      // Add date filter into the fetchMorePayload from the vue store Vue.set(state, "monitorChatFilters", monitorChatFilter);
      const monitorChatFilters = this.monitorChatFilters;
      const filterDates = monitorChatFilters?.dates;

      this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", true);
      // When selecting a chat, it will run this function once.
      // we want the first run to include dates, and the rest to run without dates (Running pagination based on the earliest date)
      // the key (dates) is used to filter the interaction by date range
      const includeDates = fetchMorePayload?.offset === 0 && !!filterDates;
      const payloadWithDates = includeDates
        ? Object.assign({}, fetchMorePayload, { dates: filterDates })
        : fetchMorePayload;
      // Fetching older data need to get date from the oldest interaction
      if(chatObject.interactions.length>0){
        const sortedInteractions = _.orderBy(
          chatObject.interactions,
          ["date_created"],
          ["asc"]
        );
        payloadWithDates.date = sortedInteractions[0].date_created;
      }
      return this.$store
        .dispatch("FETCH_MORE_MONITOR_LIVECHAT_INTERACTION", payloadWithDates)
        .then((newInteractions) => {

          const currentInteractions = chatObject.interactions;
          const updatedInteractions = _.concat(currentInteractions, newInteractions);
          const updatedLivechat = Object.assign({}, chatObject, {
            interactions: updatedInteractions,
          });

          this.$store.commit("UPDATE_MONITOR_CHAT", { updatedLivechat });

          this.$nextTick(this.checkForMoreMessages);

          if (!shouldScrollToBottom) {
            this.$nextTick(this.scrollToTop);
          } else {
            this.$nextTick(this.scrollToBottom);
          }
        })
        .catch((error) => {
          this.$notify.error({
            title: "Error",
            message: "Encountered error fetching monitor chat history",
            position: "bottom-right",
          });
        })
        .finally(() => {
          this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", false);
        });
    },
    fetchMoreNewerMonitorChatMessages(
      fetchMorePayload,
      chatObject = this.selectedChat
    ) {
      // order by ascending
      const currentInteractions = _.orderBy(
        chatObject?.interactions || [],
        ["date_created"],
        ["asc"]
      );

      //Even though we're in Fetch Newer function
      // we need to add fetchingNewer to true to for the sql query
      fetchMorePayload.fetchingNewer = true;
      //even if empty the date will be consumed by moment object and it will return a valid date. (todays date)
      fetchMorePayload.date = currentInteractions[currentInteractions.length - 1].date_created;
      this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", true);
      this.$store
        .dispatch("FETCH_MORE_NEWER_MONITOR_LIVECHAT_INTERACTION", fetchMorePayload)
        .then((newInteractions) => {
          const fetchedAll = newInteractions.length === 0;

          const currentInteractions = chatObject.interactions;
          const updatedInteractions = _.concat(currentInteractions, newInteractions);

          const updatedLivechat = Object.assign({}, chatObject, {
            interactions: updatedInteractions,
          });

          this.$store.commit("UPDATE_MONITOR_CHAT", { updatedLivechat });

          this.$nextTick(this.checkForMoreMessages);
          this.$nextTick(this.scrollToBottom);
        })
        .catch((error) => {
          this.$notify.error({
            title: "Error",
            message: "Encountered error fetching monitor chat history",
            position: "bottom-right",
          });
        })
        .finally(() => {
          this.setBusyState(chatObject?.RowKey, "isLoadingMoreConversation", false);
        });

    },
    // Call a minimal fetch to see if there's any newer data, if not can hide the button
    checkForMoreMessages() {
      // Get the selected chat
      const chatObject = this.selectedChat;
      if (!chatObject) {
        console.warn('No chat selected');
        return;
      }
      let sortedInteractions = []
      if(chatObject.interactions.length>0){
        sortedInteractions = _.orderBy(
          chatObject.interactions,
          ["date_created"],
          ["asc"]
        );
      }
      if(sortedInteractions.length === 0){
        console.warn('No interactions found');
        return;
      }

      const startDate = sortedInteractions[0].date_created;
      const endDate = sortedInteractions[sortedInteractions.length - 1].date_created;
      // Prepare payload for fetching newer messages
      const fetchMorePayload = {
        userId: chatObject.user_id,
        partitionKey: chatObject.PartitionKey,
        rowKey: chatObject.RowKey,
        startDate,
        endDate,
        limit: 1 // Only need to know if there are newer messages, not fetch them all
      };

      // Set loading state
      this.setBusyState(chatObject.RowKey, 'isLoadingMoreConversation', true);

      // Fetch newer messages
      this.$store
        .dispatch('CHECK_MONITOR_LIVECHAT_INTERACTION', fetchMorePayload)
        .then((newInteractions) => {
          const hasNewerMessages = newInteractions.hasNewerInteractions;
          const hasOlderMessages = newInteractions.hasOlderInteractions;
          this.showNewerConversationButton = hasNewerMessages;
          this.showMoreConversationButton = hasOlderMessages;
        })
        .catch((error) => {
          console.error('Error checking for newer messages:', error);
          this.$notify.error({
            title: 'Error',
            message: 'Failed to check for newer messages',
            position: 'bottom-right',
          });
        })
        .finally(() => {
          this.setBusyState(chatObject.RowKey, 'isLoadingMoreConversation', false);
        });
    },
    setWebChatTypingIndicator(isTyping) {
      if (this.isAgentTyping !== isTyping) {
        // Only execute this if not same status.
        this.isAgentTyping = isTyping;
        this.$store.dispatch("TOGGLE_WEBCHAT_TYPING_INDICATOR", {
          chat: this.selectedChat,
          isTyping,
        });
      }
    },
    closeCurrentChat() {
      this.$store.commit("SET_SELECTED_CHAT_ID", null);
      this.$store.commit("SELECT_MONITOR_CHAT", {
        partitionKey: null,
        rowKey: null,
        type: "monitor",
      });
    },
    async startAndConnectCobrowse() {
      this.startCobrowse();
      const userId = this.selectedChat.user_id;
      const agent = this.$store.state.profile.email;
      const { url } = await this.$rest("get", `webchat_cobrowse?user=${userId}&agent=${agent}`);
      this.cobrowseFrameUrl = url;
    },
    toggleCobrowse(status) {
      const chat = this.selectedChat;
      this.$store
        .dispatch("TOGGLE_COBROWSE", {
          chat,
          status,
        })
        .then(() => {
          this.inCobrowseSession = status;
          this.cobrowseDrawerOpen = status;
        })
        .catch(() => {
          this.inCobrowseSession = false;
          this.cobrowseDrawerOpen = false;
        });
    },
    startCobrowse() {
      if (!this.inCobrowseSession) {
        this.toggleCobrowse(true);
      }
    },
    stopCobrowse() {
      if (this.inCobrowseSession) {
        this.toggleCobrowse(false);
      }
    },
    minimizeOrCloseCobrowseDrawer(done) {
      this.$confirm("Are you sure want to stop co-browse session?", "Confirmation", {
        distinguishCancelAndClose: true,
        confirmButtonText: "Yes, stop",
        cancelButtonText: "No, minimize",
        showClose: false,
      })
        .then(() => {
          this.stopCobrowse();
          done();
        })
        .catch(() => {
          this.cobrowseDrawerOpen = false;
        });
    },
    handleWindowBlur: function () {
      this.isUserOnSite = false;
    },
    handleWindowFocus: function () {
      this.isUserOnSite = true;
    },
    playSound() {
      const audio = new Audio(notificationUnreadMessage);
      audio.play();
    },
    isBusy(chatId) {
      const state = this.busyStates[chatId] || {};
      return (
        state.isResolvingChat ||
        state.uploading ||
        state.isLoadingMoreConversation ||
        state.isJoiningChat ||
        state.isLeavingChat
      );
    },
    setBusyState(chatId, key, value) {
      if (!this.busyStates[chatId]) {
        this.$set(this.busyStates, chatId, {});
      }
      this.$set(this.busyStates[chatId], key, value);
    },
    startResizing(e) {
      if (!this.canResizeSidebar) return;
      this.isResizing = true;
      this.startX = e.clientX;
      this.initialWidth = this.sidebarWidth; // Store initial width at start
      document.addEventListener("mousemove", this.resize);
      document.addEventListener("mouseup", this.stopResizing);
    },

    resize(e) {
      if (!this.isResizing || !this.canResizeSidebar) return;
      const clientX = e.clientX;
      const deltaX = this.startX - clientX;
      let newWidth = this.initialWidth + deltaX;

      const minWidth = 300;
      const maxWidth = 500;
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      this.sidebarWidth = newWidth;
    },

    stopResizing() {
      this.isResizing = false;
      this.startX = 0;
      this.initialWidth = 0;
      document.removeEventListener("mousemove", this.resize);
      document.removeEventListener("mouseup", this.stopResizing);
    },
  },
  mounted() {
    this.handleViewportResize();
    window.addEventListener("resize", this.handleViewportResize);
    this.replyMessage = LocalStorageManager.getLivechatDraft(
      this.$store.state.profile.email,
      this.selectedChat.user_id
    );
    if (isNotificationEnable()) {
      this.isNotificationEnable = true;
    }
    if (this.selectedChat?.userDetails) {
      this.userDetails = {
        ...this.selectedChat.userDetails,
      };
    }

    this.isIE = this.isIEOrEdge();
    this.$nextTick(this.scrollToBottom);

    this.unsubcribleWSReconnectedEvent = listenGraphQLWebsocket(
      GRAPHQL_WEBSOCKET_STATE.RECONNECTED,
      () => {
        this.canAgentReceiveNewMessage = true;
        this.showMoreConversation(this.selectedChat, true, true);
      }
    );
    this.unsubcribleWSErrorEvent = listenGraphQLWebsocket(
      GRAPHQL_WEBSOCKET_STATE.ERROR,
      (error) => {
        this.canAgentReceiveNewMessage = false;
      }
    );
    this.unsubcribleWSDisconnectedEvent = listenGraphQLWebsocket(
      GRAPHQL_WEBSOCKET_STATE.DISCONNECTED,
      () => {
        this.canAgentReceiveNewMessage = false;
      }
    );
  },
  created() {
    window.addEventListener("blur", this.handleWindowBlur);
    window.addEventListener("focus", this.handleWindowFocus);
  },
  beforeDestroy() {
    isFunction(this.unsubcribleWSReconnectedEvent) && this.unsubcribleWSReconnectedEvent();
    isFunction(this.unsubcribleWSErrorEvent) && this.unsubcribleWSErrorEvent();
    isFunction(this.unsubcribleWSDisconnectedEvent) && this.unsubcribleWSDisconnectedEvent();
    window.removeEventListener("resize", this.handleViewportResize);
    window.removeEventListener("blur", this.handleWindowBlur);
    window.removeEventListener("focus", this.handleWindowFocus);
    this.stopResizing();
  },
  beforeMount() {
    _.delay(() => this.isFileUploadable(), 100);
  },
  watch: {
    selectedChatInteractions: {
      handler: function (newVal) {
        if (newVal.length > 0) {
          if (this.skipNextInteractionAutoScroll) {
            this.skipNextInteractionAutoScroll = false;
          } else if(this.activeTabName != "monitor"){
            this.$nextTick(this.scrollToBottom);
          }
        }

        if (!_.get(this, "$store.state.modules.webchat.soundNotification")) {
          return;
        }

        const userId = newVal[newVal.length - 1].user_id;
        const chatSession = this.chatSessions.find((chat) => chat.user_id === userId);
        if (!chatSession) {
          this.chatSessions.push({ user_id: userId, chatLength: newVal.length });
          return;
        }
        if (chatSession.chatLength === newVal.length) {
          return;
        }
        const index = this.chatSessions.findIndex((chat) => chat.user_id === userId);
        this.chatSessions[index].length = newVal.length;
        if (newVal[newVal.length - 1].type == "agent") {
          return;
        } else {
          if (this.isUserOnSite) {
            return;
          }
          this.playSound();
        }
      }
    },
    selectedChat(selectedChat, previousChat) {
      const selectedChatId = _.get(selectedChat, "RowKey");
      const previousChatId = _.get(previousChat, "RowKey");
      if (selectedChatId === previousChatId) {
        return;
      }

      const hasVisitorProfileRendered = _.has(this.$refs, "visitor-profile");
      const hasSelectedChatfFormData = !_.isEmpty(this.selectedChatformData);
      const canSetFormData =
        hasVisitorProfileRendered && hasSelectedChatfFormData && this.dataCollectionFormEnabled;

      if (canSetFormData) {
        this.$refs["visitor-profile"].selectedFormData = this.selectedChatformData;
      }

      this.showMoreConversationButton = !this.isMonitorTab;
      this.showNewerConversationButton = false;

      if (selectedChat) {
        this.$nextTick(this.scrollToBottom);
      }

      this.replyMessage = LocalStorageManager.getLivechatDraft(
        this.$store.state.profile.email,
        this.selectedChat?.user_id
      ); // Reset replyMessage whenever a selectedChat changes

      if (this.selectedChat?.userDetails) {
        this.userDetails = {
          ...this.selectedChat.userDetails,
        };
      }

      if (this.selectedChat?.RowKey && !this.busyStates[selectedChat?.RowKey]) {
        this.$set(this.busyStates, selectedChat?.RowKey, {});
      }

      if (this.isMonitorTab && _.get(selectedChat, "interactions.length", 0) > 0) {
        this.$nextTick(this.checkForMoreMessages);
      }
    },
    selectedChatId: "resetFormShowingAndButtons",
    replyMessage: _.throttle(
      function (updatedMessage) {
        LocalStorageManager.updateLivechatDraft(
          this.$store.state.profile.email,
          this.selectedChat.user_id,
          updatedMessage
        );
        if (!this.selectedChat) {
          this.setWebChatTypingIndicator(false);
          return;
        }

        if (_.isEmpty(updatedMessage)) {
          this.setWebChatTypingIndicator(false);
        } else {
          this.setWebChatTypingIndicator(true);
        }
      },
      1500,
      { leading: true }
    ),
    cobrowseDrawerOpen(val) {
      if (val) {
        const drawerParent = this.$refs.cobrowseDrawer.$el;
        if (drawerParent) {
          drawerParent.className = drawerParent.className.replace(
            " cobrowse-drawer-parent-hide",
            ""
          );
        }
      } else {
        setTimeout(() => {
          const drawerParent = this.$refs.cobrowseDrawer.$el;
          if (drawerParent) {
            drawerParent.className += " cobrowse-drawer-parent-hide";
          }
        }, 500);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/colors.scss";
@keyframes border-pulse {
  0% {
    border: 2px solid rgba($color-warning, 0);
  }
  30% {
    border: 2px solid rgba($color-warning, 1);
  }
  70% {
    border: 2px solid rgba($color-warning, 1);
  }
  100% {
    border: 2px solid rgba($color-warning, 0);
  }
}

.message-border {
  padding: 0 10px;
  box-sizing: border-box;
  border: 2px solid rgba(white, 1);
}
.message-border-pulse {
  will-change: border;
  animation: border-pulse 1000ms 1 forwards;
}

@keyframes flashing {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0.911;
  }
}

.notification-flashing {
  background-color: $color-warning;
  will-change: opacity;
  animation: flashing 1500ms 3.5 forwards;
}

.more-conversation {
  text-align: center;
  cursor: pointer;
}
.more-conversation-button {
  border: 0px;
  color: rgba(0, 0, 0, 0.4);
  font-size: 12px;
}
.interactions-row {
  height: auto !important;
}

.chat-actions-header {
  padding: 8px;
  vertical-align: center;
  height: auto;
  min-width: 0;
}

.chat-actions {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

@media (min-width: 580px) and (max-width: 1024px) {
  .el-main {
    /* max-height: 55%; */
    overflow: scroll;
  }
  .el-dropdown-menu__item {
    font-size: 1.125em !important;
  }
}

@media (max-width: 850px) {
  .interactions-row {
    margin-top: 50px;

    div {
      flex-wrap: wrap;
    }
  }

  .chat-actions-header {
    padding: 8px 0px;
  }
}

.visitorprofile {
  height: 100%;
  width: 0;
  position: absolute;
  z-index: 10;
  overflow: auto;
  transition: 0.3s;
  right: 0;
}

#mainchat {
  height: 100%;
  margin-left: auto;
  margin-right: 0;
  border: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.chat-window {
  padding: 10px;
  overflow-x: hidden;
  border-top: 1px solid $color-light;
  display: flex;
  position: relative;
  flex: 1;
  min-height: 0;
  .chat-widget {
    height: 100%;
    margin: 10px;
    padding: 20px;
    flex: 1;
    overflow-y: scroll;
  }

  .user-profile {
    padding: 20px 20px 20px 0;
    height: fit-content;
    border: 1px solid $color-light;
    position: sticky;
    top: 0;
    margin: 10px;
    max-width: 40%;
  }
}

.secondary-header {
  overflow-y: hidden;
}

.message-textbox {
  min-height: 40px !important;
  padding: 8px 15px;
  font-family: inherit;
  border-radius: 5px;
  color: inherit;
  display: flex;
  align-items: center;
}

.message-textbox:focus {
  outline: none !important;
  border: 1px solid $color-primary;
}



.el-card.livechatTextBoxSection .el-card__body {
  padding: 5px !important;
}

.el-card.livechatTextBoxSection {
  border-radius: 0px;
  background-color: $color-light;
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  vertical-align: middle;
}

.error-message {
  color: $color-danger;
}

.el-drawer__wrapper {
  height: 500px;
  width: 100%;
  top: 0;
  left: 0;
}

.cobrowse-banner {
  font-size: 14px;
  font-weight: bold;
  padding: 8px;
  vertical-align: center;
  height: auto !important;
  background-color: $color-warning;
  color: #fff;

  span {
    text-decoration: underline;
    cursor: pointer;
  }

  i {
    cursor: pointer;
    position: absolute;
    right: 10px;
    font-weight: bold;
    font-size: 18px;
    transition: 0.7s;
  }
  i:hover {
    transition: 0.7s;
    transform: rotate(180deg);
  }
}

#mainchat {
  ::v-deep .el-loading-mask {
    z-index: 10;
  }
}

.connection-alert-box {
  margin-left: 20px;
  margin-bottom: 10px;
}

.connection-alert-timestamp {
  font-size: 9px;
  font-style: italic;
}

.connection-alert-detail {
  margin-bottom: 5px;
}

.disabled-conversation-container {
  width: 100%;
  height: 100%;
  background-color: $color-dark;
  display: flex;
  align-items: center;
  justify-content: center;
}

.disabled-conversation-text {
  color: $color-light;
  text-align: center;
}
</style>

<style>
.livechat-message-suggestion li {
  white-space: normal;
}

.livechat-message-suggestion {
  left: 0px;
  width: 99% !important;
  position: absolute;
  bottom: 45px;
  z-index: 10;
  max-width: 100%;
}

.livechat-message-suggestion .el-autocomplete-suggestion__wrap {
  max-height: 200px;
  overflow-y: auto;
}

/* Ensure autocomplete suggestions appear above the input */
.livechat-message-suggestion {
  z-index: 2000;
}

/* Ensure proper spacing and scrolling for expanded input */
.keyreply-livechat-textbox-section .el-textarea.is-focus,
.keyreply-livechat-textbox-section .el-autocomplete.is-focus {
  position: relative;
  z-index: 1;
}

/* Ensure input area doesn't get cut off at bottom */
.el-main.chat-window {
  padding-bottom: 20px !important;
}

.cobrowse-drawer-hide {
  display: none;
}

.cobrowse-drawer-parent-hide {
  height: 0 !important;
  width: 0 !important;
}
</style>

<style lang="scss">
@import "../../assets/scss/colors.scss";
#mainchat .el-tabs__content {
  height: 90%;
}

.keyreply-livechat-textbox-section {
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 50px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.keyreply-livechat-textbox-section textarea {
  outline: none;
  border: none;
  font-size: 14px;
  width: 100%;
  resize: none;
  min-height: 20px;
}

.keyreply-livechat-textbox-section textarea:focus {
  outline: none;
  border: none !important;
  box-shadow: none !important;
}

/* Element UI input styling for auto-expand */
.keyreply-livechat-textbox-section .el-textarea__inner,
.keyreply-livechat-textbox-section .el-input__inner {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  padding: 0;
  font-size: 14px;
  resize: none;
  background: transparent;
}

.keyreply-livechat-textbox-section .el-textarea,
.keyreply-livechat-textbox-section .el-autocomplete {
  width: 100%;
}

.keyreply-livechat-textbox-section .el-textarea__inner:focus,
.keyreply-livechat-textbox-section .el-input__inner:focus {
  border: none !important;
  box-shadow: none !important;
}

.resizer {
  width: 5px;
  cursor: ew-resize;
  background: #fff;
  transition: background-color 0.2s ease;
}

.chat-sidebar-shell {
  display: flex;
  height: 100%;
  flex-shrink: 0;
  min-width: 0;
}

.chat-interaction-sidebar {
  min-width: 260px;
}

.chat-interaction-sidebar .el-tabs__content {
  height: calc(100% - 55px);
  overflow-y: auto;
}

.tool-drawer {
  height: 100%!important;
}
.drawer-container {
  width: 40%!important;
}
</style>
